import * as React from "react";

// =============================================================================
// RevoGrid Context
// =============================================================================
//
// RevoGrid renders cell templates OUTSIDE the React/Inertia tree, which means
// hooks like usePage() or useOrganizationRoutes() will fail with:
// "usePage must be used within the Inertia component"
//
// This context provides a way to pass data (like organizationSlug) to RevoGrid
// cells without relying on Inertia's context.
//
// Usage:
// 1. Wrap your RevoGrid in RevoGridProvider with the slug
// 2. Use TemplateWithContext instead of Template for cells that need the slug
// 3. In the cell, use useRevoGridContext() instead of useOrganizationRoutes()
//
// =============================================================================

export interface RevoGridContextValue {
  /** Current organization slug for building tenant-scoped routes */
  organizationSlug: string;
}

const RevoGridContext = React.createContext<RevoGridContextValue | null>(null);

// =============================================================================
// Hook
// =============================================================================

/**
 * Access RevoGrid context data in cell templates.
 * Use this instead of usePage() or useOrganizationRoutes() in RevoGrid cells.
 *
 * @throws Error if used outside RevoGridProvider
 */
export function useRevoGridContext(): RevoGridContextValue {
  const context = React.useContext(RevoGridContext);
  if (!context) {
    throw new Error(
      "useRevoGridContext must be used within a RevoGridProvider. " +
        "Make sure your RevoGrid is wrapped with RevoGridProvider and use TemplateWithContext for cells.",
    );
  }
  return context;
}

// =============================================================================
// Provider
// =============================================================================

interface RevoGridProviderProps {
  children: React.ReactNode;
  organizationSlug: string;
}

/**
 * Provides context data to RevoGrid cell templates.
 *
 * Wrap your RevoGrid component with this provider to enable cells
 * to access organization data without relying on Inertia's context.
 *
 * @example
 * ```tsx
 * const routes = useOrganizationRoutes();
 *
 * return (
 *   <RevoGridProvider organizationSlug={routes.organizationSlug}>
 *     <RevoGrid ... />
 *   </RevoGridProvider>
 * );
 * ```
 */
export function RevoGridProvider({
  children,
  organizationSlug,
}: RevoGridProviderProps) {
  const value = React.useMemo<RevoGridContextValue>(
    () => ({ organizationSlug }),
    [organizationSlug],
  );

  return (
    <RevoGridContext.Provider value={value}>
      {children}
    </RevoGridContext.Provider>
  );
}
